import { getSnapshot } from 'mobx-state-tree';
import React, { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Button,
  defaultColors,
  Dialog,
  FormField,
  Grid,
  Heading,
  InputGroup,
  majorScale,
  TagInput,
  TextArea,
  useToast,
} from '../../npaper';
import { AddressFormControls } from '../../npaper/AddressFormControls';
import { useStores } from '../../stores';
import {
  IPurchaseOrder,
  IPurchaseOrderSnapshotIn,
  PurchaseOrderStatus,
} from '../../stores/PurchaseOrderStore';
import { CompanySelect } from '../companies/CompanySelect';

export interface IPurchaseOrderDetailsDialogProps {
  isShown: boolean;
  onClose: (createdPO?: IPurchaseOrder) => void;
  purchaseOrder?: IPurchaseOrder;
}

export const PurchaseOrderDetailsDialog: React.FC<IPurchaseOrderDetailsDialogProps> =
  ({ onClose, purchaseOrder, ...props }) => {
    const intl = useIntl();
    const toaster = useToast();
    const [isShown, setIsShown] = useState(props.isShown);

    const { purchaseOrderStore, authStore } = useStores();
    const methods = useForm<IPurchaseOrderSnapshotIn>();

    const { register, control, handleSubmit, setValue, reset, formState } =
      methods;

    useEffect(() => {
      setIsShown(props.isShown);
    }, [props.isShown]);

    useEffect(() => {
      if (!isShown) return;
      if (purchaseOrder) {
        reset(getSnapshot(purchaseOrder));
      } else {
        const defaultAddress = authStore.currentOrganisation?.address;
        reset({
          delivery_address: defaultAddress || undefined,
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [purchaseOrder, isShown]);

    const close = (createdPO?: IPurchaseOrder) => {
      setIsShown(false);
      onClose(createdPO);
    };

    const onSubmit = async (values: IPurchaseOrderSnapshotIn) => {
      if (purchaseOrder) {
        // Update new po
        try {
          const data = values as any;
          if (purchaseOrder.status !== PurchaseOrderStatus.DRAFT) {
            delete data.delivery_address;
          }
          await purchaseOrderStore.updateItem(purchaseOrder, data);
          close();
        } catch (err) {
          toaster.showDefaultError(err);
        }
      } else {
        // Create new po
        try {
          const newPO = await purchaseOrderStore.createItem(values);
          close(newPO);
        } catch (err) {
          toaster.showDefaultError(err);
        }
      }
    };

    return (
      <Dialog
        title={
          purchaseOrder
            ? intl.formatMessage({ defaultMessage: 'Edit purchase order' })
            : intl.formatMessage({
                defaultMessage: 'Create new purchase order',
              })
        }
        isShown={isShown}
        onClose={onClose}
        actions={
          <>
            <Button onClick={() => close()} disabled={formState.isSubmitting}>
              <FormattedMessage defaultMessage="Cancel" />
            </Button>

            <Button
              primary
              onClick={handleSubmit(onSubmit)}
              isLoading={formState.isSubmitting}
            >
              {purchaseOrder ? (
                <FormattedMessage defaultMessage="Save changes" />
              ) : (
                <FormattedMessage defaultMessage="Create purchase order" />
              )}
            </Button>
          </>
        }
      >
        <FormProvider {...methods}>
          {(!purchaseOrder ||
            purchaseOrder.status === PurchaseOrderStatus.DRAFT) && (
            <Grid
              padding={majorScale(2)}
              borderBottom={`2px solid ${defaultColors.gray[100]}`}
            >
              <Heading size={300} className="col-span-12">
                <FormattedMessage defaultMessage="Details" />
              </Heading>

              <FormField
                className="col-span-9"
                label={intl.formatMessage({ defaultMessage: 'Supplier' })}
                inline={true}
                display={purchaseOrder ? 'none' : 'flex'}
              >
                <Controller
                  control={control}
                  name="supplier"
                  defaultValue={null}
                  render={({ onChange, value }) => (
                    <CompanySelect
                      onSelect={(company) => {
                        onChange(company.id);
                        if (company.currency) {
                          setValue('currency', company.currency);
                        }
                      }}
                      filter={{
                        supplier: true,
                      }}
                      selected={value}
                    />
                  )}
                />
              </FormField>

              <FormField
                className="col-span-9"
                label="Leveringsadresse"
                inline={true}
              >
                <AddressFormControls name="delivery_address" />
              </FormField>
              <FormField className="col-span-9" label="Attention" inline>
                <InputGroup
                  placeholder="Skriv Att. her"
                  ref={register}
                  name="attention"
                />
              </FormField>
            </Grid>
          )}
          <Grid padding={majorScale(2)}>
            <Heading size={300} className="col-span-12">
              <FormattedMessage defaultMessage="Administration" />
            </Heading>

            <FormField className="col-span-9" inline label="Reference">
              <InputGroup
                placeholder="En reference for indkøbsordren"
                ref={register}
                name="reference"
              />
            </FormField>

            <FormField className="col-span-9" inline label="Beskrivelse">
              <TextArea
                placeholder="En kort beskrivelse"
                maxLength={500}
                ref={register}
                name="description"
              />
            </FormField>

            <FormField className="col-span-9" label="Tags" inline>
              <Controller
                control={control}
                name="tags"
                defaultValue={[]}
                render={({ onChange, value, onBlur }) => (
                  <TagInput value={value} onChange={onChange} onBlur={onBlur} />
                )}
              />
            </FormField>
          </Grid>
        </FormProvider>
      </Dialog>
    );
  };
